import React, { useState } from 'react';
import styled from 'styled-components';

const StarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const Star = styled.button<{ filled: boolean; hovered: boolean }>`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: var(--border-radius-sm);
  transition: transform 0.2s ease, color 0.2s ease;
  color: ${({ filled, hovered }) => 
    filled || hovered ? '#fbbf24' : '#d1d5db'};
  
  &:hover {
    transform: scale(1.1);
  }
  
  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const RatingText = styled.span`
  margin-left: 0.5rem;
  color: var(--color-gray-600);
  font-size: 0.9rem;
`;

interface StarRatingProps {
  currentRating?: number;
  onRate?: (rating: number) => void;
  disabled?: boolean;
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const StarRating: React.FC<StarRatingProps> = ({
  currentRating = 0,
  onRate,
  disabled = false,
  showText = true,
  size = 'md'
}) => {
  const [hoverRating, setHoverRating] = useState<number>(0);
  const maxRating = 5; // 1-5 stars as per requirements
  
  const handleStarClick = (rating: number) => {
    if (disabled || !onRate) return;
    onRate(rating);
  };
  
  const handleMouseEnter = (rating: number) => {
    if (disabled) return;
    setHoverRating(rating);
  };
  
  const handleMouseLeave = () => {
    setHoverRating(0);
  };
  
  const getFontSize = () => {
    switch (size) {
      case 'sm': return '1.25rem';
      case 'lg': return '2rem';
      default: return '1.5rem';
    }
  };
  
  return (
    <StarContainer>
      {[...Array(maxRating)].map((_, index) => {
        const rating = index + 1;
        const isFilled = rating <= (hoverRating || currentRating);
        const isHovered = hoverRating > 0 && rating <= hoverRating;
        
        if (disabled) {
          // Render non-interactive stars for display only
          return (
            <span
              key={rating}
              style={{ 
                fontSize: getFontSize(),
                color: isFilled ? '#fbbf24' : '#d1d5db',
                padding: '0.25rem'
              }}
            >
              ★
            </span>
          );
        }

        return (
          <Star
            key={rating}
            filled={isFilled}
            hovered={isHovered}
            disabled={disabled}
            onClick={() => handleStarClick(rating)}
            onMouseEnter={() => handleMouseEnter(rating)}
            onMouseLeave={handleMouseLeave}
            style={{ fontSize: getFontSize() }}
            title={`Rate ${rating} stars`}
            aria-label={`Rate ${rating} stars`}
          >
            ★
          </Star>
        );
      })}
      {showText && (
        <RatingText>
          {hoverRating || currentRating} / {maxRating} stars
        </RatingText>
      )}
    </StarContainer>
  );
};

export default StarRating;